'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Check, ChevronDown, TrendingUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterCheckbox,
  FilterLabel,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import { RangeSliderFilter } from '../RangeSliderFilter';

interface SizeFilterGroupProps {
  t: TFunction<'translation', undefined>;
  sizeCount: number;
  clearSize: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  lockOf: (control: string) => 'user' | 'pro' | null;
  revenueMin: number | undefined;
  revenueMax: number | undefined;
  setRevenueMin: Dispatch<SetStateAction<number | undefined>>;
  setRevenueMax: Dispatch<SetStateAction<number | undefined>>;
  extents: {
    revenue_max: number;
    aktiva_max: number;
    profit_min: number;
    profit_max: number;
    employees_max: number;
  };
  revenueIncludeNull: boolean;
  setRevenueIncludeNull: Dispatch<SetStateAction<boolean>>;
  aktivaMin: number | undefined;
  aktivaMax: number | undefined;
  setAktivaMin: Dispatch<SetStateAction<number | undefined>>;
  setAktivaMax: Dispatch<SetStateAction<number | undefined>>;
  profitMin: number | undefined;
  profitMax: number | undefined;
  setProfitMin: Dispatch<SetStateAction<number | undefined>>;
  setProfitMax: Dispatch<SetStateAction<number | undefined>>;
  employeesMin: number | undefined;
  employeesMax: number | undefined;
  setEmployeesMin: Dispatch<SetStateAction<number | undefined>>;
  setEmployeesMax: Dispatch<SetStateAction<number | undefined>>;
  showFinancialYear: boolean;
  yearPickerOpen: boolean;
  setYearPickerOpen: Dispatch<SetStateAction<boolean>>;
  financialYear: number | undefined;
  setFinancialYear: Dispatch<SetStateAction<number | undefined>>;
  yearOptions: number[];
  facetCount: (facet: string, value: string) => number | undefined;
  hasYearlyMetadata: boolean;
  setHasYearlyMetadata: Dispatch<SetStateAction<boolean>>;
}

export function SizeFilterGroup({
  t,
  sizeCount,
  clearSize,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  lockOf,
  revenueMin,
  revenueMax,
  setRevenueMin,
  setRevenueMax,
  extents,
  revenueIncludeNull,
  setRevenueIncludeNull,
  aktivaMin,
  aktivaMax,
  setAktivaMin,
  setAktivaMax,
  profitMin,
  profitMax,
  setProfitMin,
  setProfitMax,
  employeesMin,
  employeesMax,
  setEmployeesMin,
  setEmployeesMax,
  showFinancialYear,
  yearPickerOpen,
  setYearPickerOpen,
  financialYear,
  setFinancialYear,
  yearOptions,
  facetCount,
  hasYearlyMetadata,
  setHasYearlyMetadata,
}: SizeFilterGroupProps) {
  return (
    <FilterGroup
      id="size"
      icon={TrendingUp}
      label={t('search.sidebar.groups.size', {
        defaultValue: 'Size & financials',
      })}
      activeCount={sizeCount}
      onClear={clearSize}
      open={openFilterGroup === 'size'}
      onOpenChange={handleGroupOpenChange('size')}
      lock={groupLock('size')}
    >
      <RangeSliderFilter
        label={
          <FilterLabel
            lock={lockOf('revenueMin')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.revenue', { defaultValue: 'Revenue' })}
          </FilterLabel>
        }
        min={revenueMin}
        max={revenueMax}
        onChange={(min, max) => {
          setRevenueMin(min);
          setRevenueMax(max);
        }}
        maxPos={extents.revenue_max}
        employees={false}
        lock={lockOf('revenueMin')}
      />

      {/* Only meaningful once a revenue band is picked — on its own
                it is not a filter, and the backend ignores it. Shown only
                then so it doesn't read as an independent toggle. It is
                also not counted in `sizeCount`: it modifies the revenue
                filter rather than being one. */}
      {(revenueMin !== undefined || revenueMax !== undefined) && (
        <label className="flex items-start gap-2 cursor-pointer select-none -mt-0.5">
          <Checkbox
            className="mt-0.5"
            checked={revenueIncludeNull}
            disabled={!!lockOf('revenueMin')}
            onCheckedChange={(v) => setRevenueIncludeNull(v === true)}
          />
          <span className="text-xs text-muted-foreground leading-snug">
            {t('search.sidebar.revenueIncludeNull', {
              defaultValue: 'Also include companies with no revenue on file',
            })}
          </span>
        </label>
      )}

      {/* Bilanzsumme — a peer of revenue, not a replacement. It is the
                broadest size axis the product has (822,065 companies vs
                revenue's 77,126) because most German filers publish a
                balance sheet without a full P&L, so a user who gets nothing
                from a revenue band will usually get results here. */}
      <RangeSliderFilter
        label={
          <FilterLabel lock={lockOf('aktivaMin')} className={FIELD_LABEL_CLASS}>
            {t('search.sidebar.aktiva', {
              defaultValue: 'Balance sheet total',
            })}
          </FilterLabel>
        }
        min={aktivaMin}
        max={aktivaMax}
        onChange={(min, max) => {
          setAktivaMin(min);
          setAktivaMax(max);
        }}
        maxPos={extents.aktiva_max}
        employees={false}
        lock={lockOf('aktivaMin')}
      />

      <RangeSliderFilter
        label={
          <FilterLabel lock={lockOf('profitMin')} className={FIELD_LABEL_CLASS}>
            {t('search.sidebar.profit', { defaultValue: 'Profit' })}
          </FilterLabel>
        }
        min={profitMin}
        max={profitMax}
        onChange={(min, max) => {
          setProfitMin(min);
          setProfitMax(max);
        }}
        maxPos={extents.profit_max}
        maxAbsNeg={Math.abs(extents.profit_min)}
        employees={false}
        lock={lockOf('profitMin')}
      />

      <RangeSliderFilter
        label={
          <FilterLabel
            lock={lockOf('employeesMin')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.employees', { defaultValue: 'Employee Count' })}
          </FilterLabel>
        }
        min={employeesMin}
        max={employeesMax}
        onChange={(min, max) => {
          setEmployeesMin(min);
          setEmployeesMax(max);
        }}
        maxPos={extents.employees_max}
        employees={true}
        lock={lockOf('employeesMin')}
      />

      {/* Financial year — only rendered when the parent exposes the
                year setter; otherwise the picker is meaningless.

                Now backed by `mv_search.financial_years @> ARRAY[Y]`, i.e.
                "the company filed a report covering Y". It used to compare
                against the single latest-filing year, which meant picking
                2020 selected the ~31,021 companies that *stopped* filing in
                2020 rather than the ~484,503 that filed for it. */}
      {showFinancialYear && (
        <div className="space-y-1.5">
          <FilterLabel
            lock={lockOf('financialYear')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.financialYear', {
              defaultValue: 'Has report for year',
            })}
          </FilterLabel>
          <Popover
            open={lockOf('financialYear') ? false : yearPickerOpen}
            onOpenChange={setYearPickerOpen}
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={!!lockOf('financialYear')}
                className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
              >
                <div className="flex flex-wrap gap-1 w-full">
                  {financialYear ? (
                    <Badge
                      variant="secondary"
                      className="text-xs font-normal gap-1 pr-1 border border-foreground"
                    >
                      <span className="font-mono">{financialYear}</span>
                      <X
                        size={12}
                        className="cursor-pointer hover:bg-muted rounded-sm"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setFinancialYear(undefined);
                        }}
                      />
                    </Badge>
                  ) : (
                    <span className={FIELD_PLACEHOLDER_CLASS}>
                      {t('search.sidebar.selectYear')}
                    </span>
                  )}
                  <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command>
                <CommandList className="max-h-[272px]">
                  <CommandGroup>
                    {yearOptions.map((y) => (
                      <CommandItem
                        key={y}
                        value={String(y)}
                        onSelect={() => {
                          setFinancialYear(y);
                          setYearPickerOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            financialYear === y ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="font-mono text-xs">{y}</span>
                        <FacetCount
                          count={facetCount('financial_year', String(y))}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Filings-only toggle — backed by hasYearlyMetadata. Useful as a
                quick "only show companies with financials" gate when the user
                cares about revenue/profit comparability. */}
      <FilterCheckbox
        checked={hasYearlyMetadata}
        onCheckedChange={setHasYearlyMetadata}
        lock={lockOf('hasYearlyMetadata')}
        label={t('search.sidebar.hasYearlyMetadata', {
          defaultValue: 'Only companies with filings',
        })}
        count={facetCount('has_filings', 'true')}
      />
    </FilterGroup>
  );
}
